import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	inject,
} from '@angular/core';
import { VgApiService, VgCoreModule } from '@videogular/ngx-videogular/core';
import { VgControlsModule } from '@videogular/ngx-videogular/controls';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';
import { PipesModule } from '../../pipes/pipes.module';
import { isNullOrUndefined } from '@pineapplelab/util';
import { PermissionsEnum } from '../../enums/permissions.enum';
import { AuthService } from '../../services/auth/auth.service';
import { ISubtitleMetadata } from '../../models/util/subtitleMetadata';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import Hls from 'hls.js';
import { environment } from '../../../environments/environment';
import { VideoTrackerService } from '../../services/video-tracker/video-tracker.service';
import { PlaybackDiagnosticsService } from '../../services/playback-diagnostics/playback-diagnostics.service';
import { isWatchedEnoughToComplete } from '../../utils/course-progress.utils';

@Component({
	selector: 'app-video-player',
	standalone: true,
	imports: [
		CommonModule,
		VgCoreModule,
		VgControlsModule,
		VgOverlayPlayModule,
		VgBufferingModule,
		PipesModule,
	],
	templateUrl: './video-player.component.html',
	styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

	private readonly subtitlesS3Domain = environment.subtitleS3Domain;
	private readonly subtitlesCdnDomain = environment.subtitleCdnDomain;

	private authService = inject(AuthService);
	private translateService = inject(TranslateService);
	private videoTrackerService = inject(VideoTrackerService);
	private diagnostics = inject(PlaybackDiagnosticsService);
	private videoEndEmitted = false;
	private wasPlayingBeforeSeek = false;
	private lastKnownVideoTime: number = 0;

	@Input() videoUrl: string = '';
	// Progressive (MP4) rendition used when the HLS stream cannot be recovered on a native player.
	@Input() fallbackVideoUrl: string = '';
	@Input() videoId: string = '';
	@Input() disableClicks: boolean = false;
	@Input() hideControls: boolean = false;
	@Input() resetVideo: boolean = false;
	@Input() pauseVideo: boolean = false;
	@Input() thumbnail: string;
	@Input() customClass: string = '';
	@Input() subtitles: ISubtitleMetadata[] = [];
	// Furthest second already watched in a previous session. Playback resumes there and seeking is
	// unlocked up to that point.
	@Input() startTime: number = 0;
	normalizedSubtitles: ISubtitleMetadata[] = [];
	@Output() videoEnded = new EventEmitter<boolean>();
	@Output() currentTimeChanged = new EventEmitter<number>();
	@Output() videoPaused = new EventEmitter<number>();

	@ViewChild('media', { static: true }) videoRef!: ElementRef<HTMLVideoElement>;

	api!: VgApiService;
	maxWatchedTime: number = 0;
	isSeeking: boolean = false;
	videoStarted: boolean = false;
	currentLanguage: string = null;
	subscriptions: Subscription[] = [];
	originalUrl: string = null;
	hls: Hls | null = null;
	private mediaRecoverAttempts = 0;
	private networkRecoverTimer: any = null;
	private startTimeApplied = false;
	private nativeRecoverAttempts = 0;
	private nativeStallTimer: any = null;
	private nativeFallbackUsed = false;
	private nativeListeners: Array<{ type: string; handler: EventListener }> = [];

	ngOnInit(): void {
		this.originalUrl = this.videoUrl;
		this.currentLanguage = this.translateService?.currentLang ?? this.translateService.defaultLang;
		this.normalizeSubtitlesUrls();
		this.videoUrl = this.toCdnUrl(this.videoUrl);
		this.fallbackVideoUrl = this.toCdnUrl(this.fallbackVideoUrl);
		this.videoTrackerService.startSession(this.videoId);
	}

	private toCdnUrl(url: string): string {
		if (!url) {
			return url;
		}
		if (url.includes(environment.videoTranscodedUrl)) {
			return url.replace(environment.videoTranscodedUrl, environment.videoCloudTranscodedUrl);
		}
		if (url.includes(environment.videoOriginalUrl)) {
			return url.replace(environment.videoOriginalUrl, environment.videoCloudOriginalUrl);
		}
		return url;
	}

	ngAfterViewInit(): void {
		this.videoEndEmitted = false;

		if (this.videoRef?.nativeElement) {
			this.repositionSubtitles();
			if (this.videoUrl) {
				this.initHlsPlayer();
			}
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resetVideo'] && this.resetVideo) this.restartVideo();
		if (changes['pauseVideo'] && this.pauseVideo) this.stopVideo();
		if (changes['subtitles']) this.normalizeSubtitlesUrls();

		if (changes['videoUrl'] && !changes['videoUrl'].firstChange) {
			if (this.videoUrl) {
				if (this.hls) {
					this.hls.destroy();
					this.hls = null;
				}
				this.startTimeApplied = false;
				this.nativeRecoverAttempts = 0;
				this.nativeFallbackUsed = false;
				this.initHlsPlayer();
			}
		}
	}

	private initHlsPlayer(): void {
		const video = this.videoRef?.nativeElement;
		if (!video || !this.videoUrl) return;

		const isHls = this.videoUrl.includes('.m3u8');
		const engine = Hls.isSupported() ? 'hls.js'
			: video.canPlayType('application/vnd.apple.mpegurl') ? 'native-hls'
			: isHls ? 'none' : 'progressive';
		this.diagnostics.videoSessionStarted(this.videoId, engine, {
			startTime: this.startTime,
			seekLocked: this.disableClicks,
			hasFallback: !!this.fallbackVideoUrl && this.fallbackVideoUrl !== this.videoUrl,
		});

		if (Hls.isSupported()) {
			this.hls = new Hls({
				capLevelToPlayerSize: true,
				autoStartLoad: true,
				startLevel: 0,
				maxBufferLength: 30,
				maxBufferSize: 30 * 1000 * 1000,
				maxBufferHole: 0.5,
				lowLatencyMode: false,
				maxFragLookUpTolerance: 0.25,
				abrEwmaFastLive: 3.0,
				abrEwmaSlowLive: 9.0,
				abrEwmaFastVoD: 3.0,
				abrEwmaSlowVoD: 9.0,
				abrBandWidthFactor: 0.7,
				abrBandWidthUpFactor: 0.25,
				maxLiveSyncPlaybackRate: 1.0,
				liveSyncDurationCount: 3,
				enableWorker: true,
				backBufferLength: 30,
				xhrSetup: (xhr, url) => {
					xhr.withCredentials = false;
				}
			});

			if ((navigator as any)?.connection && (navigator as any).connection?.effectiveType === '4g') {
				this.hls.config.maxBufferLength = 60;
				this.hls.config.maxBufferSize = 50 * 1000 * 1000;
			}

			this.hls.loadSource(this.videoUrl);
			this.hls.attachMedia(video);

			this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
				video.play().catch((err) => this.onAutoplayPrevented(err));
			});

			this.hls.on(Hls.Events.FRAG_BUFFERED, () => {
				this.mediaRecoverAttempts = 0;
			});

			this.hls.on(Hls.Events.ERROR, (event, data) => {
				if (!data.fatal) return;
				const errorData = { type: data.type, details: data.details, position: video.currentTime, url: (data as any)?.url ?? null };

				switch (data.type) {
					case Hls.ErrorTypes.NETWORK_ERROR:
						console.warn('HLS fatal network error, restarting load:', data.details);
						this.diagnostics.breadcrumb('hls.js fatal network error, restarting load', errorData);
						if (!this.networkRecoverTimer) {
							this.networkRecoverTimer = setTimeout(() => {
								this.networkRecoverTimer = null;
								this.hls?.startLoad();
							}, 1000);
						}
						break;

					case Hls.ErrorTypes.MEDIA_ERROR:
						console.warn('HLS fatal media error, recovering:', data.details);
						if (this.mediaRecoverAttempts < 2) {
							this.mediaRecoverAttempts++;
							this.diagnostics.breadcrumb('hls.js media error, recovering', { ...errorData, attempt: this.mediaRecoverAttempts });
							this.hls?.recoverMediaError();
						} else {
							console.error('HLS media error unrecoverable, destroying player');
							this.diagnostics.warn('Video playback unrecoverable (hls.js media error)', errorData, { event: 'playback-dead', engine: 'hls.js' });
							this.hls?.destroy();
							this.completeIfNearlyWatched('hls.js media error');
						}
						break;

					default:
						console.error('HLS unrecoverable error, destroying player:', data);
						this.diagnostics.warn('Video playback unrecoverable (hls.js)', errorData, { event: 'playback-dead', engine: 'hls.js' });
						this.hls?.destroy();
						this.completeIfNearlyWatched('hls.js fatal error');
						break;
				}
			});
		} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
			// iPhone Safari: no Media Source Extensions, so hls.js (and all of its recovery) is unavailable
			// and the browser's own HLS player is used. It reports failures but never retries them.
			video.src = this.videoUrl;
			video.addEventListener('loadedmetadata', () => {
				video.play().catch((err) => this.onAutoplayPrevented(err));
			}, { once: true });
			this.attachNativeRecovery(video);
		}
	}

	private onAutoplayPrevented(err: any): void {
		console.warn('Autoplay was prevented:', err);
		this.diagnostics.breadcrumb('autoplay prevented', { name: err?.name ?? null, message: err?.message ?? null });
	}

	private static readonly NATIVE_STALL_TIMEOUT_MS = 15_000;
	private static readonly NATIVE_MAX_RELOADS = 2;
	// How far past the watched high-water mark a position may sit before it counts as a skip ahead.
	private static readonly SEEK_TOLERANCE_S = 1;
	// A stall this close to the end is the end: reloading the stream there only replays the stall.
	private static readonly END_STALL_WINDOW_S = 2;

	/**
	 * Completes a video whose playback died after the learner had watched nearly all of it. Without
	 * this, a stream that cannot play its closing seconds leaves the chapter unfinished for good and
	 * the learner re-watching it over and over.
	 */
	private completeIfNearlyWatched(reason: string): boolean {
		const duration = this.videoRef?.nativeElement?.duration;
		const watched = Math.max(this.maxWatchedTime, this.startTime || 0);
		if (this.videoEndEmitted || !isWatchedEnoughToComplete(watched, duration)) {
			return false;
		}
		this.videoEndEmitted = true;
		this.diagnostics.warn('Video completed after playback failed near the end', { reason, watched, duration }, { event: 'playback-end-fallback' });
		this.videoEnded.emit(true);
		return true;
	}

	private attachNativeRecovery(video: HTMLVideoElement): void {
		this.detachNativeRecovery(video);
		const armStallTimer = () => {
			if (video.paused || video.ended) {
				return;
			}
			this.clearNativeStallTimer();
			const positionAtStall = video.currentTime;
			this.nativeStallTimer = setTimeout(() => {
				this.nativeStallTimer = null;
				if (!video.paused && !video.ended && video.currentTime === positionAtStall) {
					console.warn('Native HLS playback stalled, recovering at', positionAtStall);
					this.recoverNativePlayback(video, 'stall');
				}
			}, VideoPlayerComponent.NATIVE_STALL_TIMEOUT_MS);
		};
		const listeners: Array<{ type: string; handler: EventListener }> = [
			{ type: 'error', handler: () => {
				console.warn('Native HLS media error:', video.error?.code, video.error?.message);
				this.recoverNativePlayback(video, `error:${video.error?.code ?? 'unknown'}`);
			} },
			{ type: 'stalled', handler: armStallTimer },
			{ type: 'waiting', handler: armStallTimer },
			{ type: 'playing', handler: () => this.clearNativeStallTimer() },
			{ type: 'timeupdate', handler: () => this.clearNativeStallTimer() },
			{ type: 'pause', handler: () => this.clearNativeStallTimer() },
		];
		listeners.forEach(({ type, handler }) => video.addEventListener(type, handler));
		this.nativeListeners = listeners;
	}

	private detachNativeRecovery(video: HTMLVideoElement | undefined): void {
		this.clearNativeStallTimer();
		if (video) {
			this.nativeListeners.forEach(({ type, handler }) => video.removeEventListener(type, handler));
		}
		this.nativeListeners = [];
	}

	private clearNativeStallTimer(): void {
		if (this.nativeStallTimer) {
			clearTimeout(this.nativeStallTimer);
			this.nativeStallTimer = null;
		}
	}

	// Reloads the stream at the position it died at; after repeated failures switches to the MP4.
	private recoverNativePlayback(video: HTMLVideoElement, reason: string): void {
		this.clearNativeStallTimer();
		const resumeAt = Math.max(video.currentTime || 0, this.lastKnownVideoTime || 0);
		if (isFinite(video.duration) && video.duration - resumeAt <= VideoPlayerComponent.END_STALL_WINDOW_S
			&& this.completeIfNearlyWatched(`native ${reason} at the end`)) {
			return;
		}
		let source = this.videoUrl;
		const detail = {
			reason,
			position: resumeAt,
			duration: video.duration,
			readyState: video.readyState,
			networkState: video.networkState,
			mediaErrorCode: video.error?.code ?? null,
			usingFallback: this.nativeFallbackUsed,
		};

		if (this.nativeRecoverAttempts >= VideoPlayerComponent.NATIVE_MAX_RELOADS) {
			if (this.nativeFallbackUsed || !this.fallbackVideoUrl || this.fallbackVideoUrl === this.videoUrl) {
				console.error('Native HLS playback unrecoverable, giving up');
				this.diagnostics.warn('Video playback unrecoverable (native HLS)', detail, { event: 'playback-dead', engine: 'native-hls' });
				this.completeIfNearlyWatched('native playback unrecoverable');
				return;
			}
			this.nativeFallbackUsed = true;
			this.nativeRecoverAttempts = 0;
			source = this.fallbackVideoUrl;
			console.warn('Native HLS playback failing, switching to progressive source');
			this.diagnostics.warn('Native HLS failing, switched to progressive source', detail, { event: 'playback-fallback', engine: 'native-hls' });
		}
		this.nativeRecoverAttempts++;
		this.diagnostics.warn('Native HLS playback recovery', { ...detail, attempt: this.nativeRecoverAttempts }, { event: 'playback-recovery', engine: 'native-hls' });

		const wasPlaying = !video.paused;
		video.addEventListener('loadedmetadata', () => {
			// Restoring a position at or below the watched high-water mark is always allowed by the
			// seek guard; the resume point can only ever be within already-watched footage.
			this.isSeeking = true;
			video.currentTime = resumeAt;
			setTimeout(() => (this.isSeeking = false), 500);
			if (wasPlaying) {
				video.play().catch((err) => console.warn('Resume after recovery was prevented:', err));
			}
		}, { once: true });
		video.src = source;
		video.load();
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s?.unsubscribe());
		this.subscriptions = [];

		if (this.networkRecoverTimer) {
			clearTimeout(this.networkRecoverTimer);
			this.networkRecoverTimer = null;
		}
		this.detachNativeRecovery(this.videoRef?.nativeElement);

		if (this.hls) {
			this.hls.destroy();
			this.hls = null;
		}

		const video = this.videoRef?.nativeElement;
		if (video) {
			video.pause();
			video.src = '';
			video.load();
		}
		this.videoTrackerService.endSession();
	}

	onTimeUpdate(videoElement: HTMLVideoElement): void {
		const currentTime = videoElement.currentTime;
		const duration = videoElement.duration;
		this.lastKnownVideoTime = currentTime;

		if (!this.videoEndEmitted && duration - currentTime <= 1) {
			this.videoEndEmitted = true;
			this.diagnostics.breadcrumb('video end detected', { source: 'timeupdate', currentTime, duration, maxWatchedTime: this.maxWatchedTime });
			this.videoEnded.emit(true);
		}
	}

	initVideoPlayer(event: VgApiService) {
		this.api = event;
		this.videoEndEmitted = false;

		this.api.getDefaultMedia().subscriptions.error.subscribe(e => {
			if (e) {
				this.videoUrl = this.originalUrl;
			}
		})

		const media = this.api.getDefaultMedia();
		this.subscriptions.push(
			media.subscriptions.loadedMetadata.subscribe(() => this.applyStartTime()),
			media.subscriptions.playing.subscribe(() => (this.videoStarted = true)),
			media.subscriptions.timeUpdate.subscribe(() => {
				if (this.isSeeking) {
					return;
				}
				const canFastForward = this.authService.hasPermission(PermissionsEnum.ALLOW_VIDEO_FAST_FORWARD);
				if (!canFastForward && this.api.currentTime > this.maxWatchedTime + VideoPlayerComponent.SEEK_TOLERANCE_S && this.disableClicks) {
					this.diagnostics.seekSnappedBack(this.api.currentTime, this.maxWatchedTime, 'timeupdate');
					this.api.currentTime = this.maxWatchedTime;
					return;
				}
				this.maxWatchedTime = Math.max(this.maxWatchedTime, this.api.currentTime);
				this.currentTimeChanged.emit(this.api.currentTime);
			}),
			media.subscriptions.seeked.subscribe(() => {
				// Ignore the seek this guard itself just issued, and allow the same slack as the
				// timeupdate guard: an exact comparison loops on sub-millisecond differences right
				// after the source loads, snapping the video back to itself several times.
				if (this.isSeeking || !this.disableClicks) {
					return;
				}
				const canFastForward = this.authService.hasPermission(PermissionsEnum.ALLOW_VIDEO_FAST_FORWARD);
				if (!canFastForward && this.api.currentTime > this.maxWatchedTime + VideoPlayerComponent.SEEK_TOLERANCE_S) {
					this.diagnostics.seekSnappedBack(this.api.currentTime, this.maxWatchedTime, 'seeked');
					this.isSeeking = true;
					this.api.currentTime = this.maxWatchedTime;
					setTimeout(() => (this.isSeeking = false), 500);
				}
			}),
			media.subscriptions.ended.subscribe(() => {
				if (!this.videoEndEmitted) {
					this.videoEndEmitted = true;
					this.diagnostics.breadcrumb('video end detected', { source: 'ended', currentTime: this.api.currentTime, duration: this.api.duration });
					this.videoEnded.emit(true);
				}
			})
		);
	}

	restartVideo() {
		if (this.api?.isPlayerReady) {
			this.videoTrackerService.startSession(this.videoId);
			this.videoEndEmitted = false;
			this.startTimeApplied = true;
			this.api.pause();
			this.api.currentTime = 0;
			this.api.play();
			this.maxWatchedTime = 0;
		}
	}

	// Resumes an unfinished video where the learner left it. Runs once per loaded source; a source
	// reloaded by stall recovery restores its own position instead.
	private applyStartTime(): void {
		if (this.startTimeApplied) {
			return;
		}
		this.startTimeApplied = true;
		const video = this.videoRef?.nativeElement;
		const duration = video?.duration;
		if (!video || !this.startTime || this.startTime <= 0 || !isFinite(duration) || this.startTime >= duration - 1) {
			this.diagnostics.breadcrumb('video ready', { duration: duration ?? null, resumedAt: 0, requestedStart: this.startTime });
			return;
		}
		this.diagnostics.breadcrumb('video ready', { duration, resumedAt: this.startTime, requestedStart: this.startTime });
		this.maxWatchedTime = Math.max(this.maxWatchedTime, this.startTime);
		this.isSeeking = true;
		video.currentTime = this.startTime;
		setTimeout(() => (this.isSeeking = false), 500);
	}

	stopVideo() {
		if (this.api?.isPlayerReady) {
			this.api.pause();
			this.api.currentTime = 0;
		}
	}

	private normalizeSubtitlesUrls(): void {
		this.normalizedSubtitles = (this.subtitles ?? []).map((subtitle) => ({
			...subtitle,
			url: subtitle?.url?.replace(this.subtitlesS3Domain, `${this.subtitlesCdnDomain}/subtitles`) ?? '',
		}));
	}

	private repositionSubtitles(): void {
		this.subscriptions.push(
			this.api.getDefaultMedia().subscriptions.loadedMetadata.subscribe(() => {
				const video = document.querySelector<HTMLVideoElement>("video");
				if (!video) {
					return;
				}
				const tracks = (video as any).textTracks as any;
				if (!tracks || !(tracks || []).length) {
					return;
				}

				for (const element of tracks) {
					const t = element;
					t.mode = "hidden";
					for (const cue of (t as any).cues as VTTCue[]) {
						cue.line = -3;
					}
				}

				for (const element of tracks) {
					const t = element;
					t.mode = t.language === this.currentLanguage ? "showing" : "disabled";
				}
			})
		);
	}

	get seekDisabled(): boolean {
		return this.disableClicks && !this.authService.hasPermission(PermissionsEnum.ALLOW_VIDEO_FAST_FORWARD);
	}

	get watchedPercent(): number {
		const duration = this.api?.duration;

		if (!duration || isNaN(duration) || duration <= 0) {
			return 0;
		}

		return Math.min(100, (this.maxWatchedTime / duration) * 100);
	}

	onPlay(event: any) {
		if (this.isSeeking) {
			return;
		}
		const currentTime = +event?.target?.currentTime
		this.videoTrackerService.onPlay(currentTime);
	}

	onPause(event: any) {
		if (this.isSeeking) {
			return;
		}
		if (event?.target?.ended) {
			return;
		}
		const currentTime = +event?.target?.currentTime
		this.videoTrackerService.onPause(currentTime);
		this.videoPaused.emit(Math.max(this.maxWatchedTime, currentTime || 0));
	}

	onSeeking(event: any): void {
		this.wasPlayingBeforeSeek = !event?.target?.paused;
		this.videoTrackerService.onSeeking(this.lastKnownVideoTime);
	}

	onSeeked(event: any): void {
		const currentTime = +event?.target?.currentTime
		this.videoTrackerService.onSeeked(currentTime, this.wasPlayingBeforeSeek);
	}

	onEnded(event: any) {
		if (this.isSeeking) {
			return;
		}
		const currentTime = +event?.target?.currentTime
		this.videoTrackerService.onVideoEnd(currentTime);
	}

}
